import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2, Clock, FileDown, ShieldCheck, Package, Send, ExternalLink, FileUp, CheckCircle2, FileText, Sparkles, CalendarDays, Zap, ArrowRight, BadgeCheck, Share2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import WafidPayDialog from "@/components/WafidPayDialog";
import WafidPayCard from "@/components/WafidPayCard";
import ModeUpgradeCard from "@/components/ModeUpgradeCard";
import GamcaSummaryCard from "@/components/GamcaSummaryCard";
import ShareEarnCard from "@/components/ShareEarnCard";
import SEOHead from "@/components/SEOHead";

import { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/dateUtils";
import RazorpayCheckout from "@/components/RazorpayCheckout";
import PromoOffersWidget from "@/components/PromoOffersWidget";
import api from "@/lib/api";

const statusTone = (status) => {
  const s = (status || "").toLowerCase();
  if (/(reject|cancel|fail)/.test(s)) return { bar: "bg-red-600", chip: "text-red-700 bg-red-100" };
  if (/(complete|deliver|success|approved|done)/.test(s)) return { bar: "bg-emerald-600", chip: "text-green-700 bg-green-100" };
  if (/(pending|await|hold)/.test(s)) return { bar: "bg-amber-500", chip: "text-yellow-700 bg-yellow-100" };
  if (/(way|forward|dispatch|transit|ship)/.test(s)) return { bar: "bg-cyan-600", chip: "text-cyan-700 bg-cyan-100" };
  if (/(progress|process|review|verif)/.test(s)) return { bar: "bg-blue-600", chip: "text-blue-700 bg-blue-100" };
  return { bar: "bg-[var(--brand-primary,#b1976b)]", chip: "text-[#9E855A] bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)]" };
};
const getStatusColor = (status) => statusTone(status).chip;

const COLLECTION_MODE_LABELS = {
  by_hand: "Received by hand",
  customer_courier: "Sent by courier",
  admin_pickup: "Courier pickup arranged",
};

function InfoCard({ title, icon: Icon, testId, children }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-[#E5E1DA] shadow-sm p-5">
      <p className="text-xs uppercase tracking-wide text-[var(--brand-secondary)] font-bold mb-3 flex items-center gap-1.5">
        <Icon size={13} className="text-[var(--brand-primary)]" /> {title}
      </p>
      {children}
    </div>
  );
}

function Row({ label, value, testId, mono, last }) {
  if (!value) return null;
  return (
    <div className={`flex justify-between gap-3 text-sm ${last ? "" : "border-b border-[#F3F0EA] pb-2 mb-2"}`}>
      <span className="text-[var(--brand-secondary)]">{label}</span>
      <span data-testid={testId} className={`font-medium text-[#1A1A1A] text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

const TrackBtn = ({ href, testId }) => (
  <a data-testid={testId} href={href} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-white bg-[var(--brand-primary)] hover:bg-[#9E855A] px-3 py-1.5 rounded-full transition-colors">
    <ExternalLink size={13} /> Track Courier
  </a>
);

export default function TrackApplication() {
  const [searchParams] = useSearchParams();
  const [number, setNumber] = useState(searchParams.get("number") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [otpNeeded, setOtpNeeded] = useState(false);
  const [trackOtp, setTrackOtp] = useState("");
  const [otpChannel, setOtpChannel] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  const sendTrackOtp = async (num) => {
    setOtpSending(true);
    try {
      const { data } = await api.post(`/applications/track/${num}/send-otp`, {});
      setOtpChannel(data.channel);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setOtpSending(false);
    }
  };

  const handleDocUpload = async (reqId, file) => {
    if (!file) return;
    setUploadingDocId(reqId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/applications/track/${result.application_number}/document-uploads/${reqId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/applications/track/${result.application_number}`);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setUploadingDocId(null);
    }
  };

  useEffect(() => {
    api.get("/geo/currency").then((res) => setCurrency(res.data.currency)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!result?.application_number) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/applications/track/${result.application_number}`);
        if (!data.otp_required) setResult(data);
      } catch (e) { console.error("Failed to auto-refresh tracking data", e); }
    }, 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.application_number]);

  const handleTrack = async (e, otpOverride) => {
    if (e?.preventDefault) e.preventDefault();
    if (!number.trim()) return;
    const num = number.trim().toUpperCase();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/applications/track/${num}`, { params: { otp: otpOverride || "" } });
      if (data.otp_required) {
        if (data.otp_error) {
          setError(data.otp_error);
        } else if (!otpNeeded) {
          await sendTrackOtp(num);
        }
        setOtpNeeded(true);
      } else {
        setOtpNeeded(false);
        setTrackOtp("");
        setResult(data);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("number")) {
      handleTrack({ preventDefault: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (result?.requires_payment && result.payment_status === "pending" && window.location.hash === "#pay-section") {
      document.getElementById("pay-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [result]);

  const tone = result ? statusTone(result.status) : null;
  const history = result ? [...(result.status_history || [])].reverse() : [];

  return (
    <div data-testid="track-application-page" className="min-h-[70vh] bg-[#F6F4F0]">
      <SEOHead pageKey="track" />
      <div className="bg-[#1A1A1A] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-12 sm:pb-16">
          <p className="text-xs uppercase tracking-[3px] text-[var(--brand-primary,#b1976b)] font-bold mb-2">Track Your Application</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Check Application Status</h1>
          <p className="text-sm text-gray-400 mb-6">Enter your application number to see live status, courier and payment updates.</p>
          <form onSubmit={handleTrack} className="flex gap-2 max-w-lg">
            <Input
              data-testid="track-page-input"
              placeholder="e.g. GU2607123456"
              value={number}
              onChange={(e) => setNumber(e.target.value.toUpperCase())}
              className="bg-white text-gray-900 h-12 text-base font-mono"
            />
            <Button data-testid="track-page-submit-btn" type="submit" disabled={loading} className="bg-[var(--brand-primary,#b1976b)] hover:bg-[#9E855A] text-white h-12 px-6 shrink-0 font-semibold">
              {loading ? <Loader2 className="animate-spin" size={17} /> : <><Search size={16} className="mr-1.5" /> Track</>}
            </Button>
          </form>
          {otpNeeded && !result && (
            <div data-testid="track-otp-panel" className="mt-4 max-w-lg rounded-lg border border-white/15 bg-white/5 p-4">
              <p className="text-[13px] text-gray-300 mb-2.5">
                <span className="font-bold text-white">One-time code:</span> we sent a 6-digit code to your {otpChannel === "email" ? "email" : "WhatsApp"}. Enter it below to view your application.
              </p>
              <div className="flex gap-2">
                <Input data-testid="track-otp-input" placeholder="6-digit code" inputMode="numeric" maxLength={6}
                  value={trackOtp} onChange={(e) => setTrackOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="bg-white text-gray-900 h-11 w-40 text-base font-mono tracking-widest" />
                <Button data-testid="track-otp-verify-btn" type="button" disabled={loading || trackOtp.length !== 6}
                  onClick={() => handleTrack(null, trackOtp)}
                  className="bg-[var(--brand-primary,#b1976b)] hover:bg-[#9E855A] text-white h-11 px-5 font-semibold">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : "Verify & View"}
                </Button>
              </div>
              <button type="button" data-testid="track-otp-resend-btn" disabled={otpSending}
                onClick={() => sendTrackOtp(number.trim().toUpperCase())}
                className="mt-2 text-[12px] font-semibold text-gray-300 underline disabled:opacity-50">
                {otpSending ? "Sending..." : "Resend code"}
              </button>
            </div>
          )}
          {error && <p data-testid="track-error-message" className="text-sm text-red-300 mt-3">{error}</p>}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24 -mt-6">
        {result && (
          <div data-testid="track-result-card" className="space-y-5">
            <div className="bg-white rounded-xl border border-[#E5E1DA] shadow-sm overflow-hidden">
              <div className={`${tone.bar} px-5 sm:px-6 py-4 text-white flex items-center justify-between flex-wrap gap-2`}>
                <div>
                  <p className="text-[11px] uppercase tracking-wider opacity-80 font-bold">Current Status</p>
                  <p data-testid="track-status-badge" className="font-heading text-xl font-bold">{result.status}</p>
                </div>
                <p className="text-xs opacity-90">Last updated: {formatDateTime(result.updated_at || result.created_at, { dateStyle: "medium" })}</p>
              </div>
              <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#F3F0EA]">
                <div className="px-5 py-4 flex items-center gap-3">
                  <FileText size={18} className="text-[var(--brand-primary)] shrink-0" />
                  <div><p className="text-[11px] uppercase tracking-wide text-[var(--brand-secondary)] font-bold">Application No</p>
                    <p className="font-mono font-semibold text-[#1A1A1A] text-sm">{result.application_number}</p></div>
                </div>
                <div className="px-5 py-4 flex items-center gap-3">
                  <Sparkles size={18} className="text-[var(--brand-primary)] shrink-0" />
                  <div><p className="text-[11px] uppercase tracking-wide text-[var(--brand-secondary)] font-bold">Service</p>
                    <p data-testid="track-service-name" className="font-semibold text-[#1A1A1A] text-sm">{result.service_name}</p></div>
                </div>
                <div className="px-5 py-4 flex items-center gap-3">
                  <CalendarDays size={18} className="text-[var(--brand-primary)] shrink-0" />
                  <div><p className="text-[11px] uppercase tracking-wide text-[var(--brand-secondary)] font-bold">Submitted On</p>
                    <p className="font-semibold text-[#1A1A1A] text-sm">{formatDateTime(result.created_at, { timeStyle: "short" })}</p></div>
                </div>
              </div>
              {!result.payment_hidden && (
                <div className="border-t border-[#F3F0EA] px-5 sm:px-6 py-3 flex justify-between items-center text-sm">
                  <span className="text-[var(--brand-secondary)]">Payment Status</span>
                  <span data-testid="track-payment-status" className={`font-medium capitalize text-xs px-2.5 py-1 rounded-full ${getStatusColor(result.payment_status)}`}>{result.payment_status?.replace(/_/g, " ")}</span>
                </div>
              )}
            </div>

            {result.wafid_recovered && (
              <div data-testid="wafid-recovered-card" className="rounded-xl border border-green-300 bg-green-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle2 size={20} className="text-green-600" />
                </span>
                <div className="flex-1">
                  <p className="font-heading font-bold text-green-800 text-base">Slip recovered successfully!</p>
                  <p className="text-xs text-[var(--brand-secondary)]">Your WAFID payment is complete and your appointment slip has been recovered.</p>
                </div>
                {result.wafid_report_id && (
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                    <button type="button" data-testid="wafid-recovered-download-btn"
                      onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/gamca/slip-download/${result.wafid_report_id}`; }}
                      className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-full px-5 py-2 transition-colors cursor-pointer">
                      <FileDown size={15} /> Download Slip
                    </button>
                    <a data-testid="wafid-share-whatsapp-btn"
                      href={`https://wa.me/?text=${encodeURIComponent(`GAMCA slip recovered successfully! ✅\nApplication: ${result.application_number}\nDownload the slip here: ${process.env.REACT_APP_BACKEND_URL}/api/gamca/slip-download/${result.wafid_report_id}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-white bg-[#25D366] hover:bg-[#1ebe5b] rounded-full px-5 py-2 transition-colors">
                      <Share2 size={15} /> Share on WhatsApp
                    </a>
                  </div>
                )}
              </div>
            )}
            {result.wafid_expired && (
              <div data-testid="wafid-expired-card" className="rounded-xl border border-red-300 bg-red-50 px-5 py-4 flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100">
                  <Clock size={20} className="text-red-600" />
                </span>
                <div>
                  <p className="font-heading font-bold text-red-700 text-base">Payment link expired</p>
                  <p className="text-xs text-[var(--brand-secondary)]">Slip recovery was unsuccessful. Please contact our support team for assistance.</p>
                </div>
              </div>
            )}
            {result.wafid_pay_link && !result.wafid_recovered && (
              <WafidPayCard link={result.wafid_pay_link} expiresAt={result.wafid_expires_at}
                onClose={async () => {
                  try {
                    const { data } = await api.get(`/applications/track/${result.application_number}`);
                    setResult(data);
                  } catch (e) { console.error("Failed to refresh tracking data", e); }
                }} />
            )}
            <ModeUpgradeCard track={result} onRefresh={async () => {
              try {
                const { data } = await api.get(`/applications/track/${result.application_number}`);
                setResult(data);
              } catch (e) { console.error("Failed to refresh tracking data", e); }
            }} />

            {(result.additional_services || []).length > 0 && (
              <InfoCard title="Additional Services" icon={Sparkles} testId="track-additional-services">
                {result.additional_services.map((s, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-[#F3F0EA] last:border-0">
                    <span className="text-[#1A1A1A]">{s.name}{s.price != null ? ` (₹${s.price})` : ""}</span>
                    <span className="font-medium text-[var(--brand-primary,#b1976b)]">{s.status}</span>
                  </div>
                ))}
              </InfoCard>
            )}

            {result.gamca_cancellation && (result.gamca_cancellation.medical_center || result.gamca_cancellation.slip_no) && (
              <InfoCard title="GAMCA Cancellation Details" icon={FileText} testId="track-gamca-cancellation-card">
                <Row label="Cancellation Medical Center" value={result.gamca_cancellation.medical_center} testId="track-gamca-medical-center" />
                <Row label="Cancellation Slip No" value={result.gamca_cancellation.slip_no} testId="track-gamca-slip-no" mono last />
              </InfoCard>
            )}

            {result.document_collection && (
              <InfoCard title="Document Collection" icon={Package} testId="track-document-collection-card">
                <Row label="Document" value={result.document_collection.document_name} testId="track-collection-document" />
                <Row label="Collection Method" value={COLLECTION_MODE_LABELS[result.document_collection.collection_mode] || result.document_collection.collection_mode} testId="track-collection-mode" />
                <Row label="Courier" value={result.document_collection.courier_name} testId="track-collection-courier" />
                <Row label="AWB Number" value={result.document_collection.awb_number} testId="track-collection-awb" mono last />
                {result.document_collection.tracking_link && <TrackBtn href={result.document_collection.tracking_link} testId="track-collection-tracking-link" />}
              </InfoCard>
            )}

            {result.partner_dispatch && (
              <InfoCard title="Documents Sent for Stamping" icon={Send} testId="track-partner-dispatch-card">
                <Row label="Dispatch Method" value={result.partner_dispatch.dispatch_mode === "by_hand" ? "Delivered by hand" : "Sent via courier"} testId="track-dispatch-mode" />
                <Row label="Courier" value={result.partner_dispatch.courier_name} testId="track-dispatch-courier" />
                <Row label="AWB Number" value={result.partner_dispatch.awb_number} testId="track-dispatch-awb" mono last />
                {result.partner_dispatch.tracking_link && <TrackBtn href={result.partner_dispatch.tracking_link} testId="track-dispatch-tracking-link" />}
              </InfoCard>
            )}

            {result.customer_return && (
              <InfoCard title="Document Return" icon={Package} testId="track-customer-return-card">
                <Row label="Return Method" value={result.customer_return.return_mode === "by_hand" ? "Collect by hand from our office" : "Sent to you via courier"} testId="track-return-mode" />
                <Row label="Courier" value={result.customer_return.courier_name} testId="track-return-courier" />
                <Row label="AWB Number" value={result.customer_return.awb_number} testId="track-return-awb" mono last />
                {result.customer_return.tracking_link && <TrackBtn href={result.customer_return.tracking_link} testId="track-return-tracking-link" />}
              </InfoCard>
            )}

            {result.document_requests?.enabled && result.document_requests.items?.length > 0 && (
              <InfoCard title="Documents Required From You" icon={FileUp} testId="track-document-requests-card">
                <p className="text-xs text-[var(--brand-secondary)] mb-3 -mt-1">Please upload a soft copy of each document below (JPG, PNG or PDF, max 5MB).</p>
                <div className="space-y-2">
                  {result.document_requests.items.map((it, i) => (
                    <div key={it.id} data-testid={`track-doc-request-${i}`} className="flex items-center justify-between gap-3 text-sm border border-[#E5E1DA] rounded-md px-3 py-2.5">
                      <span className="font-medium text-[#1A1A1A]">{it.name}</span>
                      {it.uploaded ? (
                        <span data-testid={`track-doc-uploaded-${i}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
                          <CheckCircle2 size={14} /> Uploaded{it.file_name ? ` — ${it.file_name}` : ""}
                        </span>
                      ) : (
                        <label data-testid={`track-doc-upload-btn-${i}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[var(--brand-primary)] hover:bg-[#9E855A] px-3 py-1.5 rounded-full cursor-pointer transition-colors shrink-0">
                          <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" disabled={uploadingDocId === it.id}
                            onChange={(e) => handleDocUpload(it.id, e.target.files[0])} />
                          {uploadingDocId === it.id ? <Loader2 size={13} className="animate-spin" /> : <FileUp size={13} />} Upload
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </InfoCard>
            )}

            {result.requires_payment && result.payment_status === "pending" && (
              <div id="pay-section" data-testid="track-payment-section" className="bg-white rounded-xl border border-[var(--brand-primary,#b1976b)]/40 shadow-sm p-6 text-center">
                <p className="text-sm text-[var(--brand-secondary)] mb-3 flex items-center justify-center gap-1.5">
                  <ShieldCheck size={15} className="text-[var(--brand-primary)]" /> Complete your payment to proceed with this application
                </p>
                <RazorpayCheckout
                  applicationId={result.id}
                  amount={currency === "USD" && result.amount_usd > 0 ? result.amount_usd : result.amount_inr}
                  currency={currency === "USD" && result.amount_usd > 0 ? "USD" : "INR"}
                  onSuccess={() => handleTrack({ preventDefault: () => {} })}
                />
              </div>
            )}

            {result.gamca_summary && <GamcaSummaryCard summary={result.gamca_summary} testPrefix="track-gamca-summary" />}
            {(result.share_earn || result.reward_coupons?.length > 0) && (
              <ShareEarnCard data={result.share_earn} coupons={result.reward_coupons} />
            )}

            {history.length > 0 && (
              <InfoCard title="Status Timeline" icon={Clock} testId="track-status-timeline">
                <div className="relative pl-1">
                  {history.map((h, i) => (
                    <div key={i} data-testid={`track-timeline-entry-${i}`} className="flex gap-3.5 pb-5 last:pb-0 relative">
                      {i < history.length - 1 && <span className="absolute left-[7px] top-5 bottom-0 w-px bg-[#E5E1DA]" />}
                      <CheckCircle2 size={16} className={`shrink-0 mt-0.5 relative z-[1] ${i === 0 ? "text-emerald-600" : "text-gray-300"}`} />
                      <div className="text-sm min-w-0 flex-1">
                        <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${getStatusColor(h.status)}`}>{h.status}</span>
                        <p className="text-xs text-[var(--brand-secondary)] mt-1">{formatDateTime(h.timestamp, { timeStyle: "short" })}</p>
                        {h.note && <p className="text-xs text-[var(--brand-secondary)] mt-1 italic">"{h.note}"</p>}
                        {h.proof_url && (
                          <a
                            data-testid={`track-timeline-proof-${i}`}
                            href={`${process.env.REACT_APP_BACKEND_URL}${h.proof_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={h.proof_name || true}
                            className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-white bg-[var(--brand-primary)] hover:bg-[#9E855A] px-3 py-1.5 rounded-full transition-colors"
                          >
                            <FileDown size={13} /> Download &amp; Print {h.proof_name ? `- ${h.proof_name}` : ""}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </InfoCard>
            )}
          </div>
        )}

        {!result && !error && !loading && (
          <div className="bg-white rounded-xl border border-[#E5E1DA] shadow-sm p-10 text-center text-sm text-[var(--brand-secondary)]">
            Enter your application number above to view its live status and progress.
          </div>
        )}
      </div>
      <PromoOffersWidget context="public" />
    </div>
  );
}
